import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuditLogs from '../components/AuditLogs';
import GDPRAdmin from '../components/GDPRAdmin';
import {
  ChevronRight,
  FileText,
  Shield,
} from 'lucide-react';

type TabType = 'audit-logs' | 'gdpr';

const AuditLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('audit-logs');
  const [hoveredTab, setHoveredTab] = useState<TabType | null>(null);

  const tabs = [
    { id: 'audit-logs' as TabType, label: 'Audit Logs', icon: FileText },
    { id: 'gdpr' as TabType, label: 'GDPR Admin', icon: Shield },
  ];

  return (
    <div
      style={{
        padding: '32px 40px',
        backgroundColor: 'var(--v2-bg)',
        minHeight: '100vh',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Breadcrumbs */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
          marginBottom: '24px',
        }}
      >
        <span
          onClick={() => navigate('/dashboard')}
          style={{
            color: 'var(--v2-primary)',
            cursor: 'pointer',
            fontWeight: 500,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        >
          Workspaces
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ color: 'var(--v2-text)', fontWeight: 500 }}>Audit & Compliance</span>
      </nav>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'var(--v2-text)',
            margin: '0 0 4px 0',
            letterSpacing: '-0.01em',
          }}
        >
          Audit & Compliance
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
          View activity logs and manage data privacy
        </p>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '0',
          borderBottom: '1px solid var(--v2-border)',
          marginBottom: '24px',
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const isHovered = hoveredTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              onMouseEnter={() => setHoveredTab(tab.id)}
              onMouseLeave={() => setHoveredTab(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                border: 'none',
                backgroundColor: 'transparent',
                color: isActive
                  ? 'var(--v2-primary)'
                  : isHovered
                    ? 'var(--v2-text-secondary)'
                    : 'var(--v2-text-tertiary)',
                fontSize: '0.875rem',
                fontWeight: isActive ? 500 : 400,
                cursor: 'pointer',
                borderBottom: isActive ? '2px solid var(--v2-primary)' : '2px solid transparent',
                marginBottom: '-1px',
                transition: 'color 0.15s ease',
              }}
            >
              <Icon size={18} strokeWidth={isActive ? 2 : 1.5} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'audit-logs' && <AuditLogs showAllWorkspaces={true} />}
      {activeTab === 'gdpr' && <GDPRAdmin />}
    </div>
  );
};

export default AuditLogsPage;
